/**
 * LinkPage Data Transformation Utility
 *
 * Transforms backend linkPage response to match frontend component expectations.
 * The backend returns data with `linkPage` config, but the frontend components
 * expect `linktreeConfig` format for styling and configuration.
 *
 * Backend structure:
 * - business.linkPage: { enabled, title, bio, socialEnabled }
 * - links: Array of BusinessLink objects (at root level, not nested in business)
 *
 * Frontend expects:
 * - business.linktreeConfig: { title, bio, background, textColor, pill, etc. }
 * - business.links: Array with icon, rightIcon, order, etc.
 */

/**
 * Transforms backend linkPage response to frontend format
 * @param {Object} backendData - Response from GET /v1/public/businesses/:slug/page
 * @returns {Object} Transformed data matching frontend expectations
 */
export const transformLinkPageResponse = (backendData) => {
  if (!backendData || !backendData.business) {
    return backendData;
  }

  const { business, links = [] } = backendData;
  const linkPage = business.linkPage || {};

  // Transform links to match frontend format
  // Backend: title, buttonText, url, position, icon
  // Frontend: title (outside the box), subtitle (inside the box - buttonText), url, order, icon
  const transformedLinks = links.map((link) => ({
    _id: link._id,
    // Use backend title as the outside title
    title: link.title || link.buttonText || "",
    url: link.url,
    enabled: link.enabled !== false,
    order: link.position || 0,
    // Use buttonText as the text inside the pill box
    subtitle: link.buttonText || link.title || "",
    rightIcon: { type: "none" }, // Default, can be customized
    icon: link.icon || { type: "none" },
    style: null,
  }));

  // Create linktreeConfig from linkPage config and business data
  // Use business.color as the background color
  const backgroundColor = business.color || "#F28C28";

  const linktreeConfig = {
    enabled: linkPage.enabled !== false,
    title: business.name,
    bio: linkPage.bio || "",
    theme: "light",
    background: {
      type: "solid",
      solidColor: backgroundColor, // Use business.color as background
      gradient: { from: backgroundColor, to: backgroundColor, angle: 180 },
      imageUrl: null,
    },
    textColor: "#FFFFFF",
    pill: {
      bgColor: "#FFFFFF",
      textColor: "#000000",
      borderColor: "#E0E0E0",
      borderWidth: 1,
      shadow: true,
      radius: 12,
    },
    socialIconColor: "#FFFFFF",
    maxWidth: 420,
    showTopBar: true,
    showShare: true,
    showNotifications: true,
    // Use socialEnabled from linkPage config
    socialEnabled: linkPage.socialEnabled !== false,
  };

  return {
    business: {
      ...business,
      linktreeConfig,
      links: transformedLinks,
    },
  };
};
